<template>
  <Drawer :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)">
    <div class="modal-header">
      <div class="modal-title">
        {{ selectedId ? 'Alterar atendimento' : 'Novo atendimento' }}
      </div>
      <button
        type="button"
        class="modal-close"
        aria-label="Fechar"
        @click="$emit('update:modelValue', false)"
      >
        <i class="material-icons">close</i>
      </button>
    </div>

    <div class="modal-body">
        <section class="modal-section">
          <div class="field">
            <label for="appointmentDate">Data</label>
            <input
              id="appointmentDate"
              type="date"
              name="appointmentDate"
              v-model="dateModel"
              :aria-invalid="!!dateError"
              aria-describedby="date-error"
            />
            <p v-if="dateError" id="date-error" class="field-error" role="alert">{{ dateError }}</p>
          </div>
          <div class="time-inputs">
            <div class="field">
              <label for="startTime">Horário início</label>
              <input
                id="startTime"
                type="time"
                name="startTime"
                v-model="startTimeModel"
                :aria-invalid="!!startTimeError"
                aria-describedby="startTime-error"
              />
              <p v-if="startTimeError" id="startTime-error" class="field-error" role="alert">{{ startTimeError }}</p>
            </div>
            <div class="field">
              <label for="endTime">Horário término</label>
              <input
                id="endTime"
                type="time"
                name="endTime"
                v-model="endTimeModel"
                :aria-invalid="!!endTimeError"
                aria-describedby="endTime-error"
              />
              <p v-if="endTimeError" id="endTime-error" class="field-error" role="alert">{{ endTimeError }}</p>
            </div>
          </div>
        </section>

        <section class="modal-section">
          <div v-if="userIsAdmin" class="field">
            <label for="lash">Lash</label>
            <v-select
              id="lash"
              :options="allLashes"
              label="name"
              v-model="lashModel"
              placeholder="Selecione a lash"
              :class="{ 'field-invalid': lashError }"
            ></v-select>
            <p v-if="lashError" id="lash-error" class="field-error" role="alert">{{ lashError }}</p>
          </div>

          <div class="field">
            <label for="client">Cliente</label>
            <v-select
              id="client"
              :options="allClients"
              label="name"
              v-model="clientModel"
              placeholder="Selecione a cliente"
              :class="{ 'field-invalid': clientError }"
            ></v-select>
            <p v-if="clientError" id="client-error" class="field-error" role="alert">{{ clientError }}</p>
          </div>
        </section>

        <section class="modal-section">
          <h4 class="section-title">Serviços</h4>
          <div class="service-select-row">
            <v-select
              :options="allServices"
              label="name"
              v-model="selectedServiceModel"
              placeholder="Adicionar serviço"
              @update:modelValue="$emit('add-service')"
            />
          </div>

          <div
            v-if="selectedServices && selectedServices.length"
            class="selected-services-list"
          >
            <div
              v-for="sel in selectedServices"
              :key="sel.service"
              class="selected-service-row"
            >
              <div class="selected-service-info">
                <div class="selected-service-name">
                  {{ serviceName(sel.service) }}
                </div>
              </div>
              <div class="selected-service-actions">
                <CurrencyInput
                  :model-value="sel.price"
                  @update:model-value="sel.price = $event"
                  class="price-input"
                  aria-label="Preço"
                />
                <button
                  type="button"
                  class="btn-icon btn-icon-danger"
                  aria-label="Remover serviço"
                  @click="$emit('delete-service', sel.service)"
                >
                  <i class="material-icons">delete_outline</i>
                </button>
              </div>
            </div>
          </div>
          <p v-if="servicesError" class="field-error" role="alert">{{ servicesError }}</p>
          <p v-else-if="!selectedServices || !selectedServices.length" class="helper-text">
            Nenhum serviço adicionado. Adicione ao menos um serviço.
          </p>
        </section>

        <AppointmentPaymentFields
          v-if="appointmentPaid"
          :paid="paidModel"
          :payment-method="paymentMethod"
          :all-payment-methods="allPaymentMethods"
          :total="total"
          :default-fee-percent="defaultFeePercent"
          @update:paid="$emit('update:paid', $event)"
          @update:payment-method="$emit('update:paymentMethod', $event)"
        />

        <section v-if="appointmentPaid && suppliesSkipped" class="modal-section">
          <h4 class="section-title">Insumos</h4>
          <p class="helper-text">Insumos foram pulados neste atendimento.</p>
        </section>

        <section v-else-if="appointmentPaid && hasSupplyActivity" class="modal-section">
          <h4 class="section-title">Insumos registrados</h4>
          <ul class="supply-summary-list">
            <li v-for="(line, idx) in supplySummary" :key="idx">
              {{ line.name }}
              <template v-if="line.quantity != null"> — {{ line.quantity }}</template>
            </li>
            <li v-for="(ev, idx) in containerEventSummary" :key="'ev-' + idx">
              {{ ev.name }}:
              <span v-if="ev.opened"> abriu {{ ev.opened }}</span>
              <span v-if="ev.depleted"> esgotou {{ ev.depleted }}</span>
            </li>
          </ul>
          <p v-if="!inventoryApplied" class="helper-text">Insumos pendentes de registro no estoque.</p>
          <div v-if="appointmentPaid && canManageSupplies" class="supply-actions">
            <button
              v-if="showRetryInventory"
              type="button"
              class="btn btn-secondary btn-sm"
              :disabled="saving || supplyActionLoading"
              @click="$emit('retry-inventory')"
            >
              {{ supplyActionLoading ? 'Registrando...' : 'Registrar no estoque' }}
            </button>
            <button
              v-if="showEditSupplies"
              type="button"
              class="btn btn-secondary btn-sm"
              :disabled="saving || supplyActionLoading"
              @click="$emit('edit-supplies')"
            >
              Editar insumos
            </button>
          </div>
        </section>

        <section v-if="!selectedId" class="modal-section">
          <h4 class="section-title">Repetir</h4>
          <div class="field">
            <label class="checkbox-label">
              <input
                type="checkbox"
                :checked="isRecurring"
                @change="$emit('update:isRecurring', ($event.target).checked)"
              />
              Repetir atendimento
            </label>
          </div>
          <template v-if="isRecurring">
            <div class="field">
              <label for="recurrenceFrequency">Frequência</label>
              <v-select
                :options="[{ label: 'Semanal', id: 'weekly' }, { label: 'A cada 2 semanas', id: 'biweekly' }, { label: 'Mensal', id: 'monthly' }]"
                :reduce="(option) => option.id"
                v-model="recurrenceFrequencyModel"
                placeholder="Frequência"
              />
            </div>
            <div class="field">
              <label>Terminar</label>
              <div class="recurrence-end-options">
                <label class="radio-label">
                  <input
                    type="radio"
                    name="recurrenceEndType"
                    value="until"
                    :checked="recurrenceEndType === 'until'"
                    @change="$emit('update:recurrenceEndType', 'until')"
                  />
                  Até a data
                </label>
                <label class="radio-label">
                  <input
                    type="radio"
                    name="recurrenceEndType"
                    value="count"
                    :checked="recurrenceEndType === 'count'"
                    @change="$emit('update:recurrenceEndType', 'count')"
                  />
                  Após ocorrências
                </label>
              </div>
              <input
                v-if="recurrenceEndType === 'until'"
                type="date"
                :value="recurrenceEndDate"
                :min="dateModel"
                aria-label="Data final da repetição"
                @input="$emit('update:recurrenceEndDate', $event.target.value)"
              />
              <input
                v-if="recurrenceEndType === 'count'"
                type="number"
                min="2"
                max="52"
                :value="recurrenceCount"
                aria-label="Número de ocorrências"
                @input="$emit('update:recurrenceCount', parseInt($event.target.value, 10) || 2)"
              />
              <p v-if="recurrenceError" class="field-error" role="alert">{{ recurrenceError }}</p>
            </div>
          </template>
        </section>

        <section class="modal-section">
          <h4 class="section-title">Observações</h4>
          <textarea
            name="comments"
            v-model="commentsModel"
            class="comments-input"
            rows="3"
            placeholder="Anote qualquer observação importante sobre o atendimento"
          ></textarea>
        </section>
      </div>

    <div class="modal-footer appointment-modal-footer">
      <button
        type="button"
        class="btn btn-secondary appointment-modal-footer__cancel"
        :disabled="saving"
        @click="$emit('update:modelValue', false)"
      >
        Cancelar
      </button>
      <div class="appointment-modal-footer__actions">
        <button
          type="button"
          class="btn"
          :class="!appointmentPaid && !isRecurring ? 'btn-secondary' : 'btn-primary'"
          :disabled="saving"
          @click="handleSave"
        >
          {{ saving ? 'Salvando...' : 'Salvar' }}
        </button>
        <button
          v-if="!appointmentPaid && !isRecurring"
          type="button"
          class="btn btn-primary"
          :disabled="saving"
          @click="handleRealize"
        >
          Realizar atendimento
        </button>
      </div>
    </div>
  </Drawer>
</template>

<script>
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Drawer from '@/components/Drawer.vue';
import CurrencyInput from '@/components/CurrencyInput.vue';
import AppointmentPaymentFields from '@/components/AppointmentPaymentFields.vue';
import { hasSupplyPayloadActivity } from '@/utils/inventoryActivity';

export default {
  name: 'AppointmentModal',
  components: { vSelect, Drawer, CurrencyInput, AppointmentPaymentFields },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    selectedId: {
      type: [String, null],
      default: null,
    },
    startDate: {
      type: String,
      default: '',
    },
    endDate: {
      type: String,
      default: '',
    },
    client: {
      type: [Object, String],
      default: '',
    },
    selectedService: {
      type: [Object, String],
      default: '',
    },
    selectedServices: {
      type: Array,
      default: () => [],
    },
    paid: {
      type: [String, Number],
      default: '',
    },
    paymentMethod: {
      type: [Object, null],
      default: null,
    },
    allPaymentMethods: {
      type: Array,
      default: () => [],
    },
    defaultFeePercent: {
      type: Number,
      default: 0,
    },
    comments: {
      type: String,
      default: '',
    },
    lash: {
      type: [Object, String],
      default: '',
    },
    allClients: {
      type: Array,
      default: () => [],
    },
    allServices: {
      type: Array,
      default: () => [],
    },
    allLashes: {
      type: Array,
      default: () => [],
    },
    userIsAdmin: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    serviceName: {
      type: Function,
      required: true,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    appointmentPaid: {
      type: Boolean,
      default: false,
    },
    inventoryApplied: {
      type: Boolean,
      default: false,
    },
    supplyUsage: {
      type: Array,
      default: () => [],
    },
    containerEvents: {
      type: Array,
      default: () => [],
    },
    suppliesSkipped: {
      type: Boolean,
      default: false,
    },
    supplyNameResolver: {
      type: Function,
      default: null,
    },
    canManageSupplies: {
      type: Boolean,
      default: false,
    },
    showEditSupplies: {
      type: Boolean,
      default: false,
    },
    showRetryInventory: {
      type: Boolean,
      default: false,
    },
    supplyActionLoading: {
      type: Boolean,
      default: false,
    },
    isRecurring: {
      type: Boolean,
      default: false,
    },
    recurrenceFrequency: {
      type: String,
      default: 'weekly',
    },
    recurrenceEndType: {
      type: String,
      default: 'until',
    },
    recurrenceEndDate: {
      type: String,
      default: '',
    },
    recurrenceCount: {
      type: Number,
      default: 2,
    },
  },
  emits: [
    'update:modelValue',
    'update:startDate',
    'update:endDate',
    'update:client',
    'update:selectedService',
    'update:paid',
    'update:paymentMethod',
    'update:comments',
    'update:lash',
    'update:isRecurring',
    'update:recurrenceFrequency',
    'update:recurrenceEndType',
    'update:recurrenceEndDate',
    'update:recurrenceCount',
    'save',
    'realize',
    'edit-supplies',
    'retry-inventory',
    'add-service',
    'delete-service',
  ],
  data() {
    return {
      dateError: '',
      startTimeError: '',
      endTimeError: '',
      lashError: '',
      clientError: '',
      servicesError: '',
      recurrenceError: '',
    };
  },
  watch: {
    modelValue(isOpen) {
      if (isOpen) {
        this.dateError = '';
        this.startTimeError = '';
        this.endTimeError = '';
        this.lashError = '';
        this.clientError = '';
        this.servicesError = '';
        this.recurrenceError = '';
      }
    },
    startDate() {
      this.dateError = '';
      this.startTimeError = '';
    },
    endDate() {
      this.endTimeError = '';
    },
    client() {
      this.clientError = '';
    },
    lash() {
      this.lashError = '';
    },
    selectedServices: {
      handler() {
        this.servicesError = '';
      },
      deep: true,
    },
    isRecurring() {
      this.recurrenceError = '';
    },
    recurrenceEndType() {
      this.recurrenceError = '';
    },
    recurrenceEndDate() {
      this.recurrenceError = '';
    },
    recurrenceCount() {
      this.recurrenceError = '';
    },
  },
  computed: {
    dateModel: {
      get() {
        return this.startDate && this.startDate.length >= 10 ? this.startDate.slice(0, 10) : '';
      },
      set(value) {
        if (!value || !value.trim()) {
          this.$emit('update:startDate', '');
          this.$emit('update:endDate', '');
          return;
        }
        const start = this.startDate && this.startDate.length >= 16 ? this.startDate.slice(11, 16) : '08:00';
        const end = this.endDate && this.endDate.length >= 16 ? this.endDate.slice(11, 16) : '10:00';
        this.$emit('update:startDate', value + 'T' + start + ':00');
        this.$emit('update:endDate', value + 'T' + end + ':00');
      },
    },
    startTimeModel: {
      get() {
        return this.startDate && this.startDate.length >= 16 ? this.startDate.slice(11, 16) : '';
      },
      set(value) {
        const date = this.startDate && this.startDate.length >= 10 ? this.startDate.slice(0, 10) : '';
        if (!value || !value.trim()) {
          this.$emit('update:startDate', date || '');
          return;
        }
        if (date) this.$emit('update:startDate', date + 'T' + value + ':00');
      },
    },
    endTimeModel: {
      get() {
        return this.endDate && this.endDate.length >= 16 ? this.endDate.slice(11, 16) : '';
      },
      set(value) {
        const date = this.endDate && this.endDate.length >= 10 ? this.endDate.slice(0, 10) : '';
        if (!value || !value.trim()) {
          this.$emit('update:endDate', date || '');
          return;
        }
        if (date) this.$emit('update:endDate', date + 'T' + value + ':00');
      },
    },
    clientModel: {
      get() {
        return this.client;
      },
      set(value) {
        this.$emit('update:client', value);
      },
    },
    selectedServiceModel: {
      get() {
        return this.selectedService;
      },
      set(value) {
        this.$emit('update:selectedService', value);
      },
    },
    paidModel() {
      const v = this.paid;
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return isNaN(n) ? null : n;
    },
    commentsModel: {
      get() {
        return this.comments;
      },
      set(value) {
        this.$emit('update:comments', value);
      },
    },
    lashModel: {
      get() {
        return this.lash;
      },
      set(value) {
        this.$emit('update:lash', value);
      },
    },
    recurrenceFrequencyModel: {
      get() {
        return this.recurrenceFrequency;
      },
      set(value) {
        this.$emit('update:recurrenceFrequency', value);
      },
    },
    hasSupplyActivity() {
      return hasSupplyPayloadActivity(this.supplyUsage, this.containerEvents);
    },
    supplySummary() {
      const lines = Array.isArray(this.supplyUsage) ? this.supplyUsage : [];
      return lines.map((line) => ({
        name: this.supplyNameResolver
          ? this.supplyNameResolver(line.supplyId)
          : line.supplyId,
        quantity: line.kind === 'quantity' ? line.quantity : null,
      }));
    },
    containerEventSummary() {
      const events = Array.isArray(this.containerEvents) ? this.containerEvents : [];
      return events
        .filter((ev) => {
          const opened = Math.max(0, Math.floor(Number(ev.opened) || 0));
          const depleted = Math.max(0, Math.floor(Number(ev.depleted) || 0));
          return opened > 0 || depleted > 0;
        })
        .map((ev) => ({
          name: this.supplyNameResolver
            ? this.supplyNameResolver(ev.supplyId)
            : ev.supplyId,
          opened: Math.max(0, Math.floor(Number(ev.opened) || 0)),
          depleted: Math.max(0, Math.floor(Number(ev.depleted) || 0)),
        }));
    },
  },
  methods: {
    validate() {
      this.dateError = '';
      this.startTimeError = '';
      this.endTimeError = '';
      this.lashError = '';
      this.clientError = '';
      this.servicesError = '';
      let valid = true;

      const dateStr = this.startDate && this.startDate.length >= 10 ? this.startDate.slice(0, 10).trim() : '';
      if (!dateStr) {
        this.dateError = 'Selecione a data do atendimento.';
        valid = false;
      }

      const startTime = this.startDate && this.startDate.length >= 16 ? this.startDate.slice(11, 16).trim() : '';
      if (!startTime) {
        this.startTimeError = 'Selecione o horário de início.';
        valid = false;
      }

      const endTime = this.endDate && this.endDate.length >= 16 ? this.endDate.slice(11, 16).trim() : '';
      if (!endTime) {
        this.endTimeError = 'Selecione o horário de término.';
        valid = false;
      }

      const hasFullStart = this.startDate && this.startDate.length >= 16;
      const hasFullEnd = this.endDate && this.endDate.length >= 16;
      if (hasFullStart && hasFullEnd && this.startDate > this.endDate) {
        this.endTimeError = 'O horário de término deve ser posterior ao horário de início.';
        valid = false;
      }

      if (this.userIsAdmin && (!this.lash || !this.lash.id)) {
        this.lashError = 'Selecione a lash.';
        valid = false;
      }

      if (!this.client || !this.client.id) {
        this.clientError = 'Selecione a cliente.';
        valid = false;
      }

      if (!this.selectedServices || !this.selectedServices.length) {
        this.servicesError = 'Adicione ao menos um serviço.';
        valid = false;
      }

      if (this.isRecurring) {
        if (this.recurrenceEndType === 'until') {
          const endDateStr = (this.recurrenceEndDate || '').trim();
          if (!endDateStr) {
            this.recurrenceError = 'Informe a data final da repetição.';
            valid = false;
          } else if (dateStr && endDateStr < dateStr) {
            this.recurrenceError = 'A data final deve ser posterior à data do atendimento.';
            valid = false;
          }
        } else if (this.recurrenceEndType === 'count') {
          const n = typeof this.recurrenceCount === 'number' ? this.recurrenceCount : parseInt(this.recurrenceCount, 10);
          if (isNaN(n) || n < 2 || n > 52) {
            this.recurrenceError = 'Informe entre 2 e 52 ocorrências.';
            valid = false;
          }
        }
      }

      return valid;
    },
    handleSave() {
      if (this.saving || !this.validate()) return;
      this.$emit('save');
    },
    handleRealize() {
      if (this.saving || !this.validate()) return;
      this.$emit('realize');
    },
  },
};
</script>

<style scoped>
.modal-body {
  flex: 1;
  min-height: 0;
}

.modal-section {
  padding: var(--space-md) 0;
  border-bottom: 1px solid var(--color-border);
}

.modal-section:last-of-type {
  border-bottom: none;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 var(--space-sm);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.field {
  margin-bottom: var(--space-md);
}

.field:last-child {
  margin-bottom: 0;
}

.time-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-sm);
}

.service-select-row {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-sm);
}

.service-select-row .v-select {
  flex: 1;
  min-width: 0;
}

.btn-add-service {
  flex-shrink: 0;
  background: var(--color-accent);
  color: #fff;
}

.btn-add-service:hover:not(:disabled) {
  background: var(--color-accent-hover);
}

.btn-add-service .material-icons {
  font-size: 18px;
}

.selected-services-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
}

.selected-service-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-sm);
  background: var(--color-accent-soft);
  border: 1px solid var(--color-border);
}

.selected-service-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--color-text);
}

.selected-service-actions {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.selected-service-actions .btn-icon {
  color: var(--color-text-secondary);
}

.selected-service-actions .btn-icon:hover {
  color: var(--color-error);
  background: var(--color-error-bg);
}

.price-input {
  width: 100px;
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin-top: var(--space-xs);
  margin-bottom: 0;
}

.field-invalid :deep(.v-select .vs__dropdown-toggle) {
  border-color: var(--color-error);
}

.helper-text {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin: var(--space-xs) 0 0;
}

.supply-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  margin-top: var(--space-sm);
}

.btn-sm {
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-md);
  margin-top: var(--space-xs);
}

.totals-column {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  flex: 1 1 120px;
}

.amount {
  font-weight: 600;
  color: var(--color-text);
}

.comments-input {
  min-height: 72px;
}

.checkbox-label,
.radio-label {
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  font-weight: normal;
  cursor: pointer;
}

.recurrence-end-options {
  display: flex;
  gap: var(--space-md);
  margin-bottom: var(--space-sm);
}

.recurrence-end-options input[type="date"],
.recurrence-end-options input[type="number"] {
  margin-top: var(--space-xs);
  display: block;
}

.appointment-modal-footer {
  justify-content: space-between;
  gap: var(--space-md);
}

.appointment-modal-footer__actions {
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  gap: var(--space-sm);
  flex: 1;
  min-width: 0;
}

.appointment-modal-footer .btn {
  text-transform: none;
  letter-spacing: normal;
  white-space: nowrap;
  min-height: 42px;
}

.appointment-modal-footer__cancel {
  flex-shrink: 0;
}

@media (max-width: 480px) {
  .appointment-modal-footer {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .appointment-modal-footer__actions {
    flex-direction: column;
  }

  .appointment-modal-footer .btn {
    width: 100%;
  }
}
</style>
